import "@fontsource/montserrat/latin-400.css";
import "@fontsource/montserrat/latin-500.css";
import "@fontsource/montserrat/latin-600.css";
import "@fontsource/roboto/latin-400.css";
import "@fontsource/roboto/latin-500.css";
import "@fontsource/roboto/latin-600.css";
import "@fontsource/roboto/latin-700.css";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconArrowRight, IconShoppingBag, IconTrash } from "@tabler/icons-react";
import { buildProductQuote } from "@/services/quote";
import { getStorefrontProducts } from "@/services/product";
import { StorefrontProduct } from "@/types/api/product";
import { GroupQuote, ProductQuote } from "@/types/api/quote";
import { toBRL } from "@/utils/formatters/formatCurrency";
import { readStoreCart, StoreCartLine, writeStoreCart } from "../cart";
import { storeAssets } from "../assets";
import { StoreHeader } from "../components/StoreHeader";
import { StoreHero } from "../components/StoreHero";
import { StoreBenefits } from "../components/StoreBenefits";
import { StorePromotionItem, StorePromotions } from "../components/StorePromotions";
import { StoreCarouselProduct, StoreProductCarousel } from "../components/StoreProductCarousel";
import { StoreResults } from "../components/StoreResults";
import { StoreTestimonials } from "../components/StoreTestimonials";
import { StoreFaq } from "../components/StoreFaq";
import { StoreFooter } from "../components/StoreFooter";
import styles from "./StorePage.module.scss";

const fallbackImage = storeAssets.productBottles;

const normalize = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

export function StorePage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<StorefrontProduct[]>([]);
  const [productQuotes, setProductQuotes] = useState<Map<number, ProductQuote>>(new Map());
  const [bundles, setBundles] = useState<GroupQuote[]>([]);
  const [cart, setCart] = useState<StoreCartLine[]>(readStoreCart);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const catalog = await getStorefrontProducts();
        const active = catalog.products.filter((product) => product.isActive);
        const quotes = active.length
          ? await buildProductQuote({ productIds: active.map((product) => product.id), planDurationDays: 0 })
          : [];
        if (cancelled) return;
        setProducts(active);
        setProductQuotes(new Map(
          quotes.flatMap((quote) => quote.type === "product" && quote.product ? [[quote.product.productId, quote.product] as const] : []),
        ));
        setBundles(
          quotes.flatMap((quote) => quote.type === "group" && quote.group?.promotionAmount ? [quote.group] : []),
        );
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => writeStoreCart(cart), [cart]);

  const cartIds = useMemo(() => new Set(cart.flatMap((line) => line.productIds)), [cart]);
  const addLine = (line: StoreCartLine) => {
    setIsCartOpen(true);
    setCart((current) => {
      const withoutOverlap = current.filter((item) => !line.productIds.some((id) => item.productIds.includes(id)));
      return [...withoutOverlap, line];
    });
  };
  const removeLine = (line: StoreCartLine) => setCart((current) => current.filter((item) => !(item.type === line.type && item.id === line.id)));
  const isLineSelected = (type: StoreCartLine["type"], id: number) => cart.some((line) => line.type === type && line.id === id);
  const toggleLine = (line: StoreCartLine) => isLineSelected(line.type, line.id) ? removeLine(line) : addLine(line);

  const productImage = (product: StorefrontProduct) => product.images?.[0] || fallbackImage;
  const productPrice = (product: StorefrontProduct) => {
    const quote = productQuotes.get(product.id);
    return { originalAmount: quote?.originalAmount ?? product.amount, finalAmount: quote?.finalAmount ?? product.amount };
  };
  const productLine = (product: { id: number; name: string }): StoreCartLine => ({ type: "product", id: product.id, productIds: [product.id], name: product.name });
  const bundleLine = (bundle: GroupQuote): StoreCartLine => ({ type: "group", id: bundle.groupId, productIds: bundle.products.map((product) => product.productId), name: bundle.name });

  const promotions: StorePromotionItem[] = [
    ...products.flatMap((product) => {
      const quote = productQuotes.get(product.id);
      if (!quote?.promotionAmount) return [];
      return [{
        key: `product-${product.id}`,
        name: product.name,
        images: [productImage(product)],
        ...productPrice(product),
        selected: isLineSelected("product", product.id),
        onToggle: () => toggleLine(productLine(product)),
      }];
    }),
    ...bundles.map((bundle) => ({
      key: `group-${bundle.groupId}`,
      name: bundle.name,
      images: bundle.products.slice(0, 3).map((product) => product.images?.[0] || fallbackImage),
      originalAmount: bundle.originalAmount,
      finalAmount: bundle.finalAmount,
      selected: isLineSelected("group", bundle.groupId),
      onToggle: () => toggleLine(bundleLine(bundle)),
    })),
  ];

  const query = normalize(search);
  const carouselProducts: StoreCarouselProduct[] = products
    .filter((product) => !query || normalize(`${product.name} ${product.type} ${product.description}`).includes(query))
    .map((product) => {
      const selected = isLineSelected("product", product.id);
      return { id: product.id, name: product.name, image: productImage(product), ...productPrice(product), selected, includedInCombo: cartIds.has(product.id) && !selected };
    });

  const cartTotal = cart.reduce((total, line) => {
    if (line.type === "group") return total + (bundles.find((bundle) => bundle.groupId === line.id)?.finalAmount ?? 0);
    const product = products.find((item) => item.id === line.id);
    return total + (product ? productPrice(product).finalAmount : 0);
  }, 0);

  return (
    <div className={styles.page}>
      <StoreHeader search={search} onSearchChange={setSearch} cartCount={cart.length} cartTotal={cartTotal} onCartClick={() => setIsCartOpen(true)} />
      <main>
        <StoreHero />
        <StoreBenefits />
        <StorePromotions items={promotions} />
        <StoreProductCarousel
          products={carouselProducts}
          loading={loading}
          error={error}
          search={search}
          onToggle={(product) => toggleLine(productLine(product))}
        />
        <StoreResults />
        <StoreTestimonials />
        <StoreFaq formHref="/form" />
      </main>
      <StoreFooter />

      <aside className={`${styles.cart} ${isCartOpen ? styles.cartOpen : ""}`} aria-label="Carrinho de compras">
        <button className={styles.cartToggle} onClick={() => setIsCartOpen((open) => !open)} aria-label={`Carrinho de compras, ${cart.length} ${cart.length === 1 ? "item" : "itens"}${isCartOpen ? ", aberto" : ", fechado"}`} aria-expanded={isCartOpen}>
          <span><IconShoppingBag size={19} /> Seu carrinho</span><b>{cart.length}</b>
        </button>
        {isCartOpen && (cart.length === 0 ? <p className={styles.emptyCart}>Seu carrinho está vazio. Escolha produtos para começar.</p> : <>
          <ul>{cart.map((line) => <li key={`${line.type}-${line.id}`}><div><strong>{line.name}</strong><small>{line.type === "group" ? "Combo" : "Produto"}</small></div><button onClick={() => removeLine(line)} aria-label={`Remover ${line.name}`}><IconTrash size={17} /></button></li>)}</ul>
          <div className={styles.cartTotal}><span>Total</span><strong>{toBRL(cartTotal)}</strong></div>
          <button className={styles.checkoutButton} onClick={() => navigate("/checkout")}>Ir para o checkout <IconArrowRight size={17} /></button>
        </>)}
      </aside>
    </div>
  );
}
